import * as THREE from 'three';
import { MAP, NEON_SIGNS } from '../utils/constants';
import { isParkLot } from './mapLayout';
import { mulberry32 } from '../utils/rng';
import type { NeonLightData } from './wetRoadMaterial';

export type NeonSignHandle = {
  group: THREE.Group;
  update: (dt: number) => void;
  getNearbyLights: (x: number, z: number, radius: number) => NeonLightData[];
};

type NeonSign = {
  mesh: THREE.Mesh;
  light: null;
  position: THREE.Vector3;
  color: THREE.Color;
  baseIntensity: number;
  flickerPhase: number;
  flickerSpeed: number;
  flickerAmount: number;
};

function createNeonTexture(text: string, color: number): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, 256, 64);

  const c = new THREE.Color(color);
  const glowColor = `rgba(${Math.floor(c.r * 255)}, ${Math.floor(c.g * 255)}, ${Math.floor(c.b * 255)}, 0.5)`;
  const mainColor = `rgb(${Math.floor(c.r * 255)}, ${Math.floor(c.g * 255)}, ${Math.floor(c.b * 255)})`;

  ctx.font = 'bold 36px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.shadowColor = glowColor;
  ctx.shadowBlur = 20;
  ctx.fillStyle = mainColor;
  ctx.fillText(text, 128, 32);

  ctx.shadowBlur = 10;
  ctx.fillText(text, 128, 32);

  ctx.shadowBlur = 5;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, 128, 32);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export function createNeonSigns(seed: number = 42): NeonSignHandle {
  const group = new THREE.Group();
  group.name = 'NeonSigns';

  const rng = mulberry32(seed);
  const signs: NeonSign[] = [];

  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / GRID;

  const buildingPositions: { x: number; z: number }[] = [];
  for (let iz = 0; iz < GRID; iz++) {
    for (let ix = 0; ix < GRID; ix++) {
      if (isParkLot(ix, iz)) continue;
      const cx = -HALF + roadWithSidewalk + blockW / 2 + ix * (blockW + roadWithSidewalk);
      const cz = -HALF + roadWithSidewalk + blockW / 2 + iz * (blockW + roadWithSidewalk);
      buildingPositions.push({ x: cx, z: cz });
    }
  }

  for (let i = 0; i < buildingPositions.length; i++) {
    const j = i + Math.floor(rng() * (buildingPositions.length - i));
    [buildingPositions[i], buildingPositions[j]] = [buildingPositions[j], buildingPositions[i]];
  }

  const signCount = Math.min(NEON_SIGNS.COUNT, buildingPositions.length);

  for (let i = 0; i < signCount; i++) {
    const building = buildingPositions[i];
    const text = NEON_SIGNS.TEXTS[Math.floor(rng() * NEON_SIGNS.TEXTS.length)];
    const color = NEON_SIGNS.COLORS[Math.floor(rng() * NEON_SIGNS.COLORS.length)];

    const side = Math.floor(rng() * 4);
    const height = 15 + rng() * 35;

    let x = building.x;
    let z = building.z;
    let rotY = 0;

    const offset = blockW * 0.48;
    switch (side) {
      case 0:
        z += offset;
        rotY = 0;
        break;
      case 1:
        z -= offset;
        rotY = Math.PI;
        break;
      case 2:
        x += offset;
        rotY = Math.PI / 2;
        break;
      case 3:
        x -= offset;
        rotY = -Math.PI / 2;
        break;
    }

    const texture = createNeonTexture(text, color);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const width = 8 + text.length * 0.8;
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(width, 2),
      material,
    );
    mesh.position.set(x, height, z);
    mesh.rotation.y = rotY;
    group.add(mesh);

    signs.push({
      mesh,
      light: null,
      position: new THREE.Vector3(x, height, z),
      color: new THREE.Color(color),
      baseIntensity: 2,
      flickerPhase: rng() * Math.PI * 2,
      flickerSpeed: 2 + rng() * 8,
      flickerAmount: 0.1 + rng() * 0.3,
    });
  }

  const update = (dt: number) => {
    for (const sign of signs) {
      sign.flickerPhase += dt * sign.flickerSpeed;

      const flicker = 1 - sign.flickerAmount * 0.5 * (1 + Math.sin(sign.flickerPhase));

      const glitch = rng() < 0.002 ? 0.3 : 1;

      const mat = sign.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.8 + 0.2 * flicker * glitch;
    }
  };

  const getNearbyLights = (x: number, z: number, radius: number): NeonLightData[] => {
    const nearby: NeonLightData[] = [];
    const radiusSq = radius * radius;

    for (const sign of signs) {
      const dx = sign.position.x - x;
      const dz = sign.position.z - z;
      const distSq = dx * dx + dz * dz;

      if (distSq < radiusSq && nearby.length < 16) {
        nearby.push({
          position: sign.position,
          color: sign.color,
          intensity: sign.baseIntensity * (1 - Math.sqrt(distSq) / radius),
        });
      }
    }

    return nearby;
  };

  return { group, update, getNearbyLights };
}
